import { Injectable } from "@angular/core";
import { SearchErrorType, SearchState } from "../../../model";

@Injectable({
  providedIn: "root",
})
export class SearchUtilsService {
  static getErrorIcon(errorType?: SearchErrorType): string {
    switch (errorType) {
      case SearchErrorType.NO_RESULTS:
      case SearchErrorType.NOT_FOUND:
        return "search_off";
      case SearchErrorType.UNAUTHORIZED:
        return "lock";
      case SearchErrorType.SERVER_ERROR:
        return "error";
      case SearchErrorType.TIME_OUT_ERROR:
        return "timer_off";
      case SearchErrorType.GEOFENCING_REQUIRED:
      case SearchErrorType.GEOFENCING_AREA_TOO_LARGE:
        return "crop_free";
      default:
        return "error_outline";
    }
  }

  static getErrorIconClass(errorType?: SearchErrorType): string {
    switch (errorType) {
      case SearchErrorType.NO_RESULTS:
      case SearchErrorType.NOT_FOUND:
      case SearchErrorType.GEOFENCING_REQUIRED:
      case SearchErrorType.GEOFENCING_AREA_TOO_LARGE:
        return "landing-icon--warning";
      default:
        return "landing-icon--error";
    }
  }

  static getErrorTitle(errorType?: SearchErrorType): string {
    switch (errorType) {
      case SearchErrorType.NO_RESULTS:
      case SearchErrorType.NOT_FOUND:
        return "No Results Found";
      case SearchErrorType.UNAUTHORIZED:
        return "Access Denied";
      case SearchErrorType.SERVER_ERROR:
        return "Server Problem";
      case SearchErrorType.TIME_OUT_ERROR:
        return "Request Timed Out";
      case SearchErrorType.GEOFENCING_REQUIRED:
        return "Geofencing Required";
      case SearchErrorType.GEOFENCING_AREA_TOO_LARGE:
        return "Geofencing Area Too Large";
      default:
        return "Search Error";
    }
  }

  /**
   * Calculates normalized similarity scores and assigns heatmap colors using provided minScore and maxScore.
   * Always uses the minScore and maxScore from state for normalization.
   * @param state SearchState containing results, minScore, and maxScore
   */
  static calculateSimilarityValues(state: SearchState): void {
    if (!state?.results?.length) return;
    if (state.minScore === undefined || state.maxScore === undefined || state.maxScore === state.minScore) {
      // Log error if min/max are not set or invalid
      console.error("SearchUtilsService: minScore and maxScore must be defined and different for normalization.");
      return;
    }

    const results = state.results;
    const min = state.minScore;
    const max = state.maxScore;

    results.forEach((result) => {
      const value = result.similarity ?? 0;
      // Normalize strictly between provided min and max
      let normalized = (value - min) / (max - min);
      normalized = Math.max(0, Math.min(1, normalized));
      result.score = normalized;
      result.scoreColor = SearchUtilsService.similarityColor(normalized);
    });
  }

  /**
   * Returns a soft heatmap color for a value between 0 and 1.
   * The scale uses a 4-color palette with soft reds and greens.
   * @param value Normalized value in the range [0,1]
   * @returns Hex color string
   */
  static similarityColor(value: number): string {
    const v = Math.max(0, Math.min(1, value));

    const palette = [
      "#9e1a1a", // low (soft red)
      "#da5128", // mid-light neutral
      "#f8c152", // mid green-olive
      "#02bd20", // high (soft green)
    ];

    if (v <= 1 / 3) {
      return SearchUtilsService.interpolateColor(palette[0], palette[1], v / (1 / 3));
    } else if (v <= 2 / 3) {
      return SearchUtilsService.interpolateColor(palette[1], palette[2], (v - 1 / 3) / (1 / 3));
    } else {
      return SearchUtilsService.interpolateColor(palette[2], palette[3], (v - 2 / 3) / (1 / 3));
    }
  }

  /**
   * Linearly interpolates between two hex colors.
   * @param c1 First color hex
   * @param c2 Second color hex
   * @param t Interpolation factor (0-1)
   * @returns Interpolated hex color
   */
  static interpolateColor(c1: string, c2: string, t: number): string {
    const r1 = parseInt(c1.substring(1, 3), 16);
    const g1 = parseInt(c1.substring(3, 5), 16);
    const b1 = parseInt(c1.substring(5, 7), 16);

    const r2 = parseInt(c2.substring(1, 3), 16);
    const g2 = parseInt(c2.substring(3, 5), 16);
    const b2 = parseInt(c2.substring(5, 7), 16);

    const r = Math.round(r1 + (r2 - r1) * t);
    const g = Math.round(g1 + (g2 - g1) * t);
    const b = Math.round(b1 + (b2 - b1) * t);

    return `#${r.toString(16).padStart(2, "0")}${g.toString(16).padStart(2, "0")}${b.toString(16).padStart(2, "0")}`;
  }
}
